'use client';

import { useCallback, useRef } from 'react';
import {
  Box,
  Button2,
  Dialog,
  InlineButton,
  Stack,
  Text,
  theme,
  useId,
} from '@frameio/vapor';
import styles from './Dialog.module.css';
import FrameLogo from './frameio-logo-white.svg';
import TEXT from './generated/TEXT/OpenOnDesktopDialogContent';

TEXT.DEFINE(
  {
    /** Dialog title for the mount project flow */
    title: (projectName: string) => `Open ${projectName} on desktop...`,
    /** Short description in the first step */
    description: 'The Frame.io Drive App is required to open projects locally.',
    installInstead: (onOpenAppDownload: () => void) => (
      <>
        Need to Install?
        <InlineButton onClick={onOpenAppDownload} variant="primary">
          Download App
        </InlineButton>
      </>
    ),
    /** Inline CTA to switch to app download dialog */
    downloadAppsInstead: 'Download apps instead',
    /** Dismiss button */
    cancel: 'Close',
  },
  { InlineButton }
);

type OpenOnDesktopDialogProps = {
  projectName: string;
  isShowing: boolean;
  onClose: () => void;
  onOpenAppDownload: () => void;
};

export function OpenOnDesktopDialog({
  projectName,
  isShowing,
  onClose,
  onOpenAppDownload,
}: OpenOnDesktopDialogProps) {
  const titleId = useId();
  const initialFocusRef = useRef<HTMLButtonElement | null>(null);

  const handleDownloadAppsInstead = useCallback(() => {
    onClose();
    onOpenAppDownload();
  }, [onClose, onOpenAppDownload]);

  // note: z-index added to ensure dialog appears above other modals such as the
  // export location dialog (which may be above the export sequence dialog)

  return (
    <Dialog
      aria-labelledby={titleId}
      initialFocusRef={initialFocusRef}
      isShowing={isShowing}
      onRequestClose={onClose}
      width="280px"
      zIndex={theme.zIndices.modal + 2}
    >
      <div className={styles.dialogContent}>
        <Box
          alignItems="center"
          className={styles.frameLogoContainer}
          display="flex"
          justifyContent="center"
        >
          <FrameLogo />
        </Box>

        <Stack alignX="center" gap={8}>
          <Text
            color="primary"
            id={titleId}
            textAlign="center"
            variant="header"
          >
            {TEXT.title(projectName)}
          </Text>
          <Text color="tertiary" textAlign="center" variant="body">
            {TEXT.description}
          </Text>
          <Text color="tertiary" textAlign="center" variant="body">
            {TEXT.installInstead(handleDownloadAppsInstead)}
          </Text>
          <Button2
            ref={initialFocusRef}
            block={true}
            onPress={onClose}
            variant="secondary"
          >
            {TEXT.cancel}
          </Button2>
        </Stack>
      </div>
    </Dialog>
  );
}
